import React from "react";
import { OutputData } from "@editorjs/editorjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ImageSourceSetData } from "../../../../common/classes/ImageSourceSet";
import { SonicTopicsData } from "../../../../common/classes/SonicTopics";
import { SonicTermData } from "../../../../common/classes/SonicTerm";
import Form from "../../../../common/forms/Form";
import TagInput from "../../../../common/forms/TagInput";
import FormInput from "../../../../common/forms/FormInput";
import RichTextArea from "../../../../common/forms/RichTextArea";
import SelectMultipleTerms from "../../../../common/forms/SelectMultipleTerms";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import ActionBtn from "../../../../common/management/ActionBtn";
import Status from "../../../../common/management/detail/Status";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";
import SelenaA from "../../../../assets/img/service/selena-a.webp";
import {
  RequestState,
  genNotificationColor,
  genNotificationMessage,
} from "../../../../utils/RequestState";
import { TermType } from "../../../../utils/useTerm";
import {
  ValType,
  getErrorMessage,
} from "../../../../utils/ValidationFunctions";
import styles from "./DatabaseDetail.module.scss";
import SonicLink from "../../../../common/routing/SonicLink";

type Props = {
  topic: SonicTopicsData;
  author: { name: string; email: string; roles: Array<string> };
  mainImage?: File | ImageSourceSetData;
  article?: OutputData;
  deleteMainImage: () => void;
  isLoading: boolean;
  selectStatus?: string;
  setSelectStatus: (value: string, name: string) => void;
  handleUpdate: () => void;
  handleInputChange: (value: File | string | undefined, name: string) => void;
  type_options?: Array<[string, string]>;

  // MyGame
  games?: Array<SonicTermData>;
  handleGames: (value: Array<SonicTermData>) => void;

  facilityOptions: [string, string][];

  showStatusMenu: boolean;
  handleStatusMenu: () => void;
  handleSaveArticle: (outputData: OutputData) => void;
  error: { required: boolean; imgs: boolean };
  requestState: RequestState;
  setRequestState: (value: RequestState) => void;
};
const TopicsDetail: React.FC<Props> = ({
  topic,
  author,
  mainImage,
  article,
  deleteMainImage,
  isLoading,
  selectStatus,
  setSelectStatus,
  handleUpdate,
  handleInputChange,
  type_options,
  games,
  handleGames,
  facilityOptions,
  showStatusMenu,
  handleStatusMenu,
  handleSaveArticle,
  error,
  requestState,
  setRequestState,
}) => {
  return (
    <div className={styles["f-databaseDetail"]}>
      {isLoading || !topic ? (
        <LoadingSpinner space={1} admin_page={true} />
      ) : (
        <>
          <h1 className={styles["f-databaseDetail__title"]}>トピックス詳細</h1>
          <div className={styles["f-databaseDetail__inner"]}>
            <div className={styles["f-databaseDetail__head"]}>
              <Status
                isVisibilityStatus={true}
                status={topic.status}
                selectStatus={selectStatus}
                showStatusMenu={showStatusMenu}
                handleStatusMenu={handleStatusMenu}
                setSelectStatus={setSelectStatus}
              />
              <div className={styles["f-databaseDetail__user"]}>
                <div className={styles["f-databaseDetail__user-head"]}>
                  <h2 className={styles["f-databaseDetail__user-head__title"]}>
                    ユーザー情報
                  </h2>
                  <SonicLink to={`/admin/member/${topic.author}`}>
                    ユーザー詳細
                  </SonicLink>
                </div>
                <div className={styles["f-databaseDetail__user-info"]}>
                  <div>
                    <p>ユーザー名</p>
                    <span>{author.name}</span>
                  </div>
                  <div>
                    <p>メールアドレス</p>
                    <span>{author.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <Form handler={() => {}}>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  id="name"
                  type="text"
                  label="タイトル"
                  label_class={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                  name="sonic_name"
                  style_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={topic.name}
                  val_type={ValType.NotNull}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="select"
                  options={type_options}
                  label="トピックスカテゴリ"
                  label_class={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                  name="sonic_topic_type"
                  select_class={styles["f-databaseDetail__form-input"]}
                  holder_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  initial_value={topic.topic_type.id}
                  placeholder="未選択"
                  val_type={ValType.NotNull}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["error-space"]}`}
              >
                <FormInput
                  type="date"
                  label="掲載開始日"
                  name="sonic_start_date"
                  handler={handleInputChange}
                  label_class={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                  style_class={styles["f-databaseDetail__form-input"]}
                  input_wrap_class={styles["f-databaseDetail__form-date-wrap"]}
                  initial_value={topic.start_date}
                  val_type={ValType.NotNull}
                />
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["vertical"]} ${styles["error-space"]}`}
              >
                <label
                  className={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                >
                  メイン画像
                </label>
                {mainImage ? (
                  <>
                    <div className={styles["imageBox"]}>
                      <img
                        src={
                          mainImage instanceof File
                            ? URL.createObjectURL(mainImage)
                            : mainImage.medium?.url
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={deleteMainImage}
                      className={`${styles["f-databaseDetail__form-fileBtn"]} ${styles["delete"]}`}
                    >
                      <FontAwesomeIcon icon={["fal", "trash-alt"]} />
                      削除する
                    </button>
                  </>
                ) : null}
                {mainImage == undefined ? (
                  <button
                    type="button"
                    className={`${styles["f-databaseDetail__form-fileBtn"]} ${styles["add"]}`}
                  >
                    <FontAwesomeIcon icon={["fal", "plus-circle"]} />
                    <FormInput
                      type="file"
                      label="追加する"
                      id="imgs"
                      name="sonic_imgs"
                      handler={handleInputChange}
                      val_type={ValType.File}
                    />
                  </button>
                ) : null}
              </div>
              <div
                className={`${styles["f-databaseDetail__form"]} ${styles["vertical"]}`}
              >
                <label
                  className={`${styles["f-databaseDetail__form-title"]} ${styles["required-mark"]}`}
                >
                  本文
                </label>
                <RichTextArea
                  richTextData={article}
                  setArticle={handleSaveArticle}
                  changeArticle={() => {
                    setRequestState(RequestState.Pending);
                  }}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <div className={styles["f-databaseDetail__form-title"]}>
                  ゲーム
                </div>
                <div className={styles["f-databaseDetail__form-box"]}>
                  <div className={styles["mygame"]}>
                    <div className={styles["mygame-title"]}>
                      {"設定中のゲーム"}
                    </div>
                    <ul className={styles["mygame-list"]}>
                      {games && games.length > 0 ? (
                        games.map((game, i) => <li key={i}>{game.name}</li>)
                      ) : (
                        <li>{"まだゲームが設定されていないようです。"}</li>
                      )}
                    </ul>
                  </div>
                  <SelectMultipleTerms
                    termType={TermType.Games}
                    termName="ゲーム"
                    buttonText="ゲームを選ぶ"
                    terms={games ? games : []}
                    handleTerms={handleGames}
                    isAdminPage={true}
                  />
                </div>
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <FormInput
                  type="select"
                  label="該当施設"
                  name="sonic_facility"
                  label_class={styles["f-databaseDetail__form-title"]}
                  select_class={styles["f-databaseDetail__form-input"]}
                  holder_class={styles["f-databaseDetail__form-input"]}
                  handler={handleInputChange}
                  options={facilityOptions}
                  initial_value={topic.facility?.id}
                />
              </div>
              <div className={styles["f-databaseDetail__form"]}>
                <TagInput
                  label="タグ"
                  label_class={styles["f-databaseDetail__form-title"]}
                  name="sonic_tags"
                  id="tags"
                  input_class={styles["f-databaseDetail__form-tagInput"]}
                  tag_class={styles["f-databaseDetail__form-tagInput__tag"]}
                  handler={handleInputChange}
                  value={topic.tags}
                />
              </div>
            </Form>
            {error.required ? (
              <div className={styles["f-databaseDetail__error"]}>
                必須項目が入力されていません
              </div>
            ) : null}
            {error.imgs ? (
              <div className={styles["f-databaseDetail__error"]}>
                {getErrorMessage(ValType.FileTotalSize)}
              </div>
            ) : null}
            <div className={styles["f-databaseDetail__bottom"]}>
              <ActionBtn
                label="保存"
                onClick={() => handleUpdate()}
                colorReversal={true}
              />
            </div>
            <div className={styles["f-databaseDetail__btn"]}>
              <ActionBtn
                label=" 保存 "
                onClick={() => handleUpdate()}
                colorReversal={true}
              />
            </div>
          </div>
        </>
      )}
      {requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor(requestState)}
          message={genNotificationMessage(requestState)}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </div>
  );
};

export default TopicsDetail;
